import {
  Button,
  Card,
  Tab,
  TabGroup,
  TabList,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRow,
} from "@tremor/react";
import Badge from "../components/Badge.jsx";
import {
  AlertTriangle,
  ArrowLeft,
  Bell,
  ChevronRight,
  Edit2,
  Pause,
  Play,
  RefreshCw,
  Trash2,
} from "lucide-react";

import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import NextCheck from "../components/NextCheck.jsx";
import { fmtDateTime, fmtInterval, fmtMs } from "../utils/format.js";
import { deleteAlertRule, fetchAlertRules } from "../api/alerts.js";
import { fetchIncidents } from "../api/incidents.js";
import {
  deleteMonitor,
  fetchMonitor,
  fetchMonitorCheckResults,
  triggerMonitorCheck,
  updateMonitor,
} from "../api/monitors.js";
import { useLocations } from "../queries/monitors.js";
import { AlertRulesDialog } from "../components/monitor/AlertRulesDialog.jsx";
import EditMonitorPanel from "../components/monitor/EditMonitorPanel.jsx";
import VisualChangePanel from "../components/monitor/VisualChangePanel.jsx";
import PageSpeedPanel from "../components/monitor/PageSpeedPanel.jsx";
import ScreenshotHistoryPanel from "../components/monitor/ScreenshotHistoryPanel.jsx";
import PlaywrightCheckDrawer from "../components/monitor/PlaywrightCheckDrawer.jsx";
import ShopifyCheckDrawer from "../components/monitor/ShopifyCheckDrawer.jsx";
import WordPressCheckDrawer from "../components/monitor/WordPressCheckDrawer.jsx";
import AdvisoryCheckDrawer, {
  ADVISORY_TYPES,
} from "../components/monitor/AdvisoryCheckDrawer.jsx";
import ResponseTimeChart from "../components/monitor/ResponseTimeChart.jsx";
import SlaTable from "../components/monitor/SlaTable.jsx";
import CheckRunsHistory from "../components/monitor/CheckRunsHistory.jsx";

import { useTranslation } from "react-i18next";

function timingKeys(type) {
  switch (type) {
    case "shopify":    return { avg: "stats.avg_journey",   chart: "response_chart.title_journey",  col: "history.journey_time" };
    case "wordpress":  return { avg: "stats.avg_ttfb",      chart: "response_chart.title_ttfb",     col: "history.ttfb" };
    case "playwright": return { avg: "stats.avg_run_time",  chart: "response_chart.title_run_time", col: "history.run_time" };
    default:           return { avg: "stats.avg_response",  chart: "response_chart.title",          col: "history.response_time" };
  }
}

function isAboveBaseline(check) {
  const baselineP50 = check?.data?.baseline_p50_ms;
  const factor = check?.data?.baseline_factor;
  const responseTime = check?.response_time_ms;

  if (
    typeof baselineP50 !== "number" ||
    typeof factor !== "number" ||
    typeof responseTime !== "number"
  ) {
    return false;
  }

  return responseTime > baselineP50 * factor;
}

const TIME_RANGES = [
  { label: "1h", hours: 1 },
  { label: "24h", hours: 24 },
  { label: "7d", hours: 168 },
  { label: "30d", hours: 720 },
];

const STATUS_COLOR = {
  up: "#22c55e",
  degraded: "#f97316",
  down: "#ef4444",
  timeout: "#ef4444",
  error: "#ef4444",
  needs_attention: "#f59e0b",
};

// A non-alertable, merchant-actionable config error (e.g. untestable product,
// password-protected store). The agent flags these with retryable === false.
function isActionableConfigCheck(check) {
  return check?.data?.retryable === false;
}

function StatCard({ label, value, sub }) {
  return (
    <Card className="py-4 px-5">
      <p className="text-xs font-medium text-gray-400 uppercase tracking-wide">
        {label}
      </p>
      <p className="mt-1 text-3xl font-bold text-gray-900">{value}</p>
      {sub && <p className="mt-0.5 text-xs text-gray-400">{sub}</p>}
    </Card>
  );
}

function StatusTimeline({ points }) {
  const { t } = useTranslation("monitor_detail");
  if (!points.length) {
    return (
      <div className="flex h-8 items-center justify-center rounded bg-gray-100 text-xs text-gray-400">
        {t("timeline.no_data")}
      </div>
    );
  }

  const w = 800;
  const h = 32;
  const n = points.length;

  return (
    <svg
      viewBox={`0 0 ${w} ${h}`}
      className="w-full rounded"
      style={{ height: h }}
      preserveAspectRatio="none"
    >
      {points.map((p, i) => (
        <rect
          key={i}
          x={(i / n) * w}
          y={0}
          width={Math.ceil(w / n) + 1}
          height={h}
          fill={
            isActionableConfigCheck(p)
              ? STATUS_COLOR.needs_attention
              : (STATUS_COLOR[p.status] ?? "#9ca3af")
          }
        />
      ))}
    </svg>
  );
}

export default function Monitor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation(["monitor_detail", "common"]);

  const [monitor, setMonitor] = useState(null);
  const [checkData, setCheckData] = useState(null);
  const [incidents, setIncidents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [rangeHours, setRangeHours] = useState(24);
  const [showEdit, setShowEdit] = useState(false);
  const [showAddRule, setShowAddRule] = useState(false);
  const [alertRules, setAlertRules] = useState([]);
  const [triggering, setTriggering] = useState(false);
  const [expandedCheck, setExpandedCheck] = useState(null);
  const triggerTimerRef = useRef(null);
  const refreshTimerRef = useRef(null);

  // Locations come from the shared cache — no per-page fetch needed.
  const { data: locationList } = useLocations();
  const locationLabels = useMemo(
    () => Object.fromEntries((locationList ?? []).map((l) => [l.slug, l.label])),
    [locationList]
  );

  useEffect(() => {
    async function load() {
      try {
        const [mon, inc, rules] = await Promise.all([
          fetchMonitor(id),
          fetchIncidents({ monitor_id: id, limit: 10 }),
          fetchAlertRules(id),
        ]);
        setMonitor(mon);
        setIncidents(inc.data);
        setAlertRules(rules);
        scheduleRefresh(mon);
      } catch {
        toast.error(t("error_load"));
        navigate("/monitors");
      } finally {
        setLoading(false);
      }
    }
    load();
    return () => clearTimeout(refreshTimerRef.current);
  }, [id, t]);

  function scheduleRefresh(mon) {
    clearTimeout(refreshTimerRef.current);
    if (!mon?.last_check_at || !mon?.interval_seconds || mon.type === "cron") return;
    const nextMs = new Date(mon.last_check_at).getTime() + mon.interval_seconds * 1000;
    // Add a small buffer so the check result is likely available
    const delay = Math.max(nextMs - Date.now() + 5000, 5000);
    refreshTimerRef.current = setTimeout(async () => {
      try {
        const fresh = await fetchMonitor(id);
        setMonitor(fresh);
        await loadCheckResults();
        scheduleRefresh(fresh);
      } catch {
        // silently ignore — user can trigger manually
      }
    }, delay);
  }

  // Reload check results whenever rangeHours changes
  useEffect(() => {
    if (!id) return;
    loadCheckResults();
  }, [id, rangeHours]);

  async function loadCheckResults() {
    const now = new Date();
    const from = new Date(now.getTime() - rangeHours * 3600 * 1000);
    try {
      const data = await fetchMonitorCheckResults(id, {
        from_time: from.toISOString(),
        to_time: now.toISOString(),
        limit: 500,
      });
      setCheckData(data);
    } catch {
      // silently ignore — charts will show empty
    }
  }

  async function handleDeleteRule(ruleId) {
    try {
      await deleteAlertRule(id, ruleId);
      setAlertRules((prev) => prev.filter((r) => r.id !== ruleId));
      toast.success(t("rules.success_remove"));
    } catch {
      toast.error(t("rules.error_remove"));
    }
  }

  async function handleToggleActive() {
    try {
      const updated = await updateMonitor(id, { is_active: !monitor.is_active });
      setMonitor(updated);
      toast.success(
        t("header.success_toggle", {
          action: updated.is_active ? t("header.resumed") : t("header.paused"),
        })
      );
    } catch {
      toast.error(t("header.error_toggle"));
    }
  }

  async function handleDelete() {
    if (!window.confirm(t("header.delete_confirm", { name: monitor.name }))) return;
    try {
      await deleteMonitor(id);
      toast.success(t("header.success_delete"));
      navigate("/monitors");
    } catch {
      toast.error(t("header.error_delete"));
    }
  }

  async function handleTrigger() {
    if (triggering) return;
    setTriggering(true);
    try {
      await triggerMonitorCheck(id);
      toast.info(t("header.success_trigger"));
      triggerTimerRef.current = setTimeout(async () => {
        await loadCheckResults();
        setTriggering(false);
        toast.success(t("header.success_refresh"));
      }, 8000);
    } catch {
      toast.error(t("header.error_trigger"));
      setTriggering(false);
    }
  }

  // Clean up pending timers on unmount
  useEffect(() => {
    return () => {
      clearTimeout(triggerTimerRef.current);
      clearTimeout(refreshTimerRef.current);
    };
  }, []);

  if (loading) {
    return (
      <div>
        <p className="text-sm text-gray-400">{t("loading")}</p>
      </div>
    );
  }

  const points = checkData?.points ?? [];
  const timing = timingKeys(monitor?.type);

  // Surface a "needs attention" banner when the most recent check result is an
  // actionable config error. Derived from points (most-recent last) so it
  // self-clears once a subsequent check passes.
  const latestIssue = (() => {
    for (let i = points.length - 1; i >= 0; i--) {
      const p = points[i];
      if (isActionableConfigCheck(p)) return p;
      // Stop at the first non-config-error result — don't surface stale banners.
      break;
    }
    return null;
  })();

  return (
    <div>
      {/* Back link */}
      <Link
        to="/monitors"
        className="mb-4 inline-flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700"
      >
        <ArrowLeft className="h-3.5 w-3.5" />
        {t("back_link")}
      </Link>

      {/* Header */}
      <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="text-2xl font-bold text-gray-900">{monitor.name}</h1>
            <Badge color="gray">{monitor.type}</Badge>
            <Badge color={monitor.is_active ? "emerald" : "gray"}>
              {monitor.is_active ? "active" : "paused"}
            </Badge>
          </div>
          <div className="mt-1.5 flex flex-wrap items-center gap-1">
            {(monitor.locations ?? []).map((r) => (
              <Badge key={r} color="teal">
                {locationLabels[r] ?? r}
              </Badge>
            ))}
            <Badge color="gray">
              {t("header.every", { interval: fmtInterval(monitor.interval_seconds) })}
            </Badge>
            <span className="text-xs text-gray-400">{monitor.target}</span>
          </div>
          {monitor.escalation_policy_id && (
            <div className="mt-1.5 flex items-center gap-1.5 text-xs text-gray-500">
              <span className="font-medium text-gray-400">
                {t("header.escalation_policy")}:
              </span>
              <code className="rounded bg-gray-100 px-1.5 py-0.5 font-mono text-xs text-gray-600">
                {String(monitor.escalation_policy_id).slice(0, 8)}…
              </code>
            </div>
          )}
          {monitor.type === "cron" && monitor.cron_token && (
            <div className="mt-2 flex flex-wrap items-center gap-2">
              <span className="text-xs text-gray-400">{t("header.ping_url")}:</span>
              <code className="min-w-0 break-all rounded bg-gray-100 px-2 py-0.5 font-mono text-xs text-gray-700">
                {window.location.origin}/api/v1/cron/{monitor.cron_token}
              </code>
              <Button
                variant="light"
                size="xs"
                onClick={() => {
                  navigator.clipboard.writeText(
                    `${window.location.origin}/api/v1/cron/${monitor.cron_token}`
                  );
                  toast.success(t("header.copied"));
                }}
              >
                {t("common:common.copy")}
              </Button>
            </div>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="secondary" icon={Bell} onClick={() => setShowAddRule(true)}>
            {t("header.alert_rules")}
          </Button>
          <Button variant="secondary" color="red" icon={Trash2} onClick={handleDelete}>
            {t("header.delete")}
          </Button>
          <Button
            variant="secondary"
            icon={monitor.is_active ? Pause : Play}
            onClick={handleToggleActive}
          >
            {monitor.is_active ? t("header.pause") : t("header.resume")}
          </Button>
          <Button variant="secondary" icon={Edit2} onClick={() => setShowEdit(true)}>
            {t("header.edit")}
          </Button>
          {monitor.type !== "cron" && (
            <Button
              icon={triggering ? RefreshCw : Play}
              disabled={triggering}
              onClick={handleTrigger}
            >
              {triggering ? t("header.checking") : t("header.run_check")}
            </Button>
          )}
        </div>
      </div>

      {/* Needs-attention banner: actionable config error on the latest run */}
      {latestIssue && (
        <div className="mb-6 flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 p-4">
          <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-amber-500" />
          <div className="min-w-0 flex-1">
            <p className="text-sm font-semibold text-amber-900">
              {t("needs_attention.title")}
            </p>
            <p className="mt-0.5 text-sm text-amber-800">
              {latestIssue.error || t("needs_attention.generic")}
            </p>
          </div>
          <button
            type="button"
            onClick={() => setExpandedCheck(latestIssue)}
            className="inline-flex shrink-0 items-center gap-1 rounded-md border border-amber-300 bg-white px-3 py-1.5 text-sm font-medium text-amber-800 hover:bg-amber-100"
          >
            {t("needs_attention.view_details")}
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>
      )}

      {/* Stats */}
      <div
        className={`mb-6 grid gap-4 grid-cols-2 ${monitor.type === "cron" ? "sm:grid-cols-3" : "sm:grid-cols-4"}`}
      >
        <StatCard
          label={t("stats.uptime")}
          value={`${checkData?.uptime_pct ?? 0}%`}
          sub={t("stats.last_range", {
            range: TIME_RANGES.find((r) => r.hours === rangeHours)?.label,
          })}
        />
        <StatCard
          label={t(timing.avg)}
          value={fmtMs(checkData?.avg_response_ms ?? null)}
        />
        <StatCard
          label={t("stats.total_checks")}
          value={checkData?.total_checks ?? 0}
        />
        {monitor.type !== "cron" && (
          <StatCard
            label={t("stats.next_check")}
            value={
              <NextCheck
                lastCheckAt={monitor.last_check_at}
                intervalSeconds={monitor.interval_seconds}
              />
            }
          />
        )}
      </div>

      {/* Time range tabs */}
      <div className="mb-4">
        <TabGroup
          index={TIME_RANGES.findIndex((r) => r.hours === rangeHours)}
          onIndexChange={(i) => setRangeHours(TIME_RANGES[i].hours)}
        >
          <TabList>
            {TIME_RANGES.map((r) => (
              <Tab key={r.hours}>{r.label}</Tab>
            ))}
          </TabList>
        </TabGroup>
      </div>

      <div className="space-y-6">
        {/* Response time chart */}
        <ResponseTimeChart monitorId={id} rangeHours={rangeHours} chartTitleKey={timing.chart} />

        {/* Status timeline */}
        <Card className="p-5">
          <h2 className="mb-3 text-sm font-semibold text-gray-700">
            {t("timeline.title")}
          </h2>
          <StatusTimeline points={points} />
          <div className="mt-2 flex items-center gap-4 text-xs text-gray-500">
            {[
              { color: "#22c55e", label: t("timeline.up") },
              { color: "#f97316", label: t("timeline.degraded") },
              { color: "#ef4444", label: t("timeline.down_error") },
              { color: "#9ca3af", label: t("timeline.unknown") },
            ].map(({ color, label }) => (
              <span key={label} className="flex items-center gap-1">
                <span
                  className="inline-block h-2.5 w-2.5 rounded-sm"
                  style={{ background: color }}
                />
                {label}
              </span>
            ))}
          </div>
        </Card>

        {monitor.type === "visual_change" && (
          <Card className="p-5">
            <h2 className="mb-3 text-sm font-semibold text-gray-700">Visual change</h2>
            <VisualChangePanel
              monitor={monitor}
              latestCheckResult={points[points.length - 1] ?? null}
            />
          </Card>
        )}

        {monitor.type === "screenshot" && (
          <ScreenshotHistoryPanel monitorId={monitor.id} />
        )}

        {monitor.type === "page_speed" && (
          <Card className="p-5">
            <h2 className="mb-3 text-sm font-semibold text-gray-700">Page speed</h2>
            <PageSpeedPanel
              monitor={monitor}
              latestCheckResult={points[points.length - 1] ?? null}
              previousGoodCheckResult={
                [...points]
                  .slice(0, -1)
                  .reverse()
                  .find((p) => p?.status === "up" && p?.data?.score != null) ?? null
              }
            />
          </Card>
        )}

        {/* SLA summary */}
        <SlaTable monitorId={id} monitorName={monitor?.name ?? ""} />

        {/* Recent incidents */}
        <Card className="p-0">
          <div className="border-b border-gray-100 px-5 py-4">
            <h2 className="text-sm font-semibold text-gray-700">
              {t("incidents.title")}
            </h2>
          </div>
          {incidents.length === 0 ? (
            <p className="px-5 py-8 text-center text-sm text-gray-400">
              {t("incidents.empty")}
            </p>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHead>
                  <TableRow>
                    <TableHeaderCell>{t("incidents.started")}</TableHeaderCell>
                    <TableHeaderCell>{t("incidents.resolved")}</TableHeaderCell>
                    <TableHeaderCell>{t("incidents.duration")}</TableHeaderCell>
                    <TableHeaderCell>{t("incidents.status")}</TableHeaderCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {incidents.map((i) => (
                    <TableRow key={i.id}>
                      <TableCell className="text-gray-500">
                        {fmtDateTime(i.started_at)}
                      </TableCell>
                      <TableCell className="text-gray-500">
                        {i.resolved_at ? fmtDateTime(i.resolved_at) : "—"}
                      </TableCell>
                      <TableCell className="text-gray-500">
                        {(() => {
                          const ms = i.resolved_at
                            ? new Date(i.resolved_at) - new Date(i.started_at)
                            : Date.now() - new Date(i.started_at);
                          const m = Math.round(ms / 60000);
                          return m < 60 ? `${m}m` : `${Math.floor(m / 60)}h ${m % 60}m`;
                        })()}
                      </TableCell>
                      <TableCell>
                        <Badge color={i.status === "open" ? "red" : "emerald"}>
                          {i.status}
                        </Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </Card>

        {/* Check history — grouped by check run */}
        <CheckRunsHistory
          monitorId={id}
          monitor={monitor}
          locationLabels={locationLabels}
          locations={monitor.locations ?? []}
          setExpandedCheck={setExpandedCheck}
          timingColKey={timing.col}
        />
      </div>

      {/* Playwright check detail drawer */}
      {expandedCheck && monitor.type === "shopify" && (
        <ShopifyCheckDrawer
          check={expandedCheck}
          monitorId={id}
          onClose={() => setExpandedCheck(null)}
        />
      )}

      {expandedCheck && monitor.type === "wordpress" && (
        <WordPressCheckDrawer
          check={expandedCheck}
          onClose={() => setExpandedCheck(null)}
        />
      )}

      {expandedCheck && ADVISORY_TYPES.has(monitor.type) && (
        <AdvisoryCheckDrawer
          check={expandedCheck}
          type={monitor.type}
          onClose={() => setExpandedCheck(null)}
        />
      )}

      {expandedCheck &&
        monitor.type !== "shopify" &&
        monitor.type !== "wordpress" &&
        !ADVISORY_TYPES.has(monitor.type) && (
          <PlaywrightCheckDrawer
            check={expandedCheck}
            onClose={() => setExpandedCheck(null)}
          />
        )}

      {/* Edit drawer */}
      {showEdit && (
        <EditMonitorPanel
          monitor={monitor}
          onClose={() => setShowEdit(false)}
          onSaved={(updated) => setMonitor(updated)}
        />
      )}

      {/* Alert rules dialog */}
      {showAddRule && (
        <AlertRulesDialog
          monitorId={id}
          rules={alertRules}
          onClose={() => setShowAddRule(false)}
          onCreated={(rule) => setAlertRules((prev) => [...prev, rule])}
          onDelete={handleDeleteRule}
        />
      )}
    </div>
  );
}
